import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { MapView } from "../directory/MapView";
import LocationSearch from "../directory/LocationSearch";
import { api, formatApiError } from "../../lib/api";

const num = (v) => (v === "" || v == null ? null : Number(v));

export default function LocationEditor({ agency, onClose, onSaved }) {
  const [pos, setPos] = useState({ latitude: agency.latitude ?? "", longitude: agency.longitude ?? "" });
  const [source, setSource] = useState("saisie");
  const [busy, setBusy] = useState(false);
  const lat = num(pos.latitude), lng = num(pos.longitude);
  const valid = lat != null && lng != null && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180 && !(lat === 0 && lng === 0);
  const pick = (src) => (p) => { setPos({ latitude: p.latitude, longitude: p.longitude }); setSource(src); };

  const save = async (clear = false) => {
    setBusy(true);
    try {
      await api.put(`/company/agencies/${agency.id}/location`, clear ? { latitude: null, longitude: null, source: "saisie" } : { latitude: lat, longitude: lng, source });
      toast.success(clear ? "Position retirée" : "Position enregistrée");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-w-2xl" data-testid="location-editor">
        <DialogHeader>
          <DialogTitle>Position de « {agency.name} »</DialogTitle>
          <DialogDescription>Cliquez sur la carte à l'emplacement exact, utilisez la position de votre appareil sur place, ou recherchez l'adresse puis ajustez.</DialogDescription>
        </DialogHeader>
        <LocationSearch prefix="location-editor" deviceLabel="Je suis sur place" onPick={(p) => pick(p.label === "Position de l'appareil" ? "appareil" : "adresse")(p)} />
        <MapView testId="location-editor-map" className="h-72" onPick={pick("carte")} points={valid ? [{ id: "pos", latitude: lat, longitude: lng, name: agency.name }] : []} selectedId="pos" />
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" step="any" value={pos.latitude} onChange={(e) => { setPos({ ...pos, latitude: e.target.value }); setSource("saisie"); }} placeholder="Latitude" data-testid="location-editor-lat" />
          <Input type="number" step="any" value={pos.longitude} onChange={(e) => { setPos({ ...pos, longitude: e.target.value }); setSource("saisie"); }} placeholder="Longitude" data-testid="location-editor-lng" />
        </div>
        {!valid && (pos.latitude !== "" || pos.longitude !== "") && <p className="text-xs text-rose-600" data-testid="location-editor-invalid">Coordonnées invalides (latitude −90 à 90, longitude −180 à 180, 0,0 refusé).</p>}
        <DialogFooter className="gap-2">
          {agency.latitude != null && <Button variant="ghost" className="text-rose-600" disabled={busy} onClick={() => save(true)} data-testid="location-editor-clear">Retirer la position</Button>}
          <Button disabled={busy || !valid} onClick={() => save(false)} data-testid="location-editor-save">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Enregistrer la position</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
